import React, { useState, useRef, useEffect } from 'react';
import { Star, Sparkles, Moon, Sun, Shield, Heart, Gem, Lock, CheckCircle, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const chargingOptions = [
  { id: 'moonlight', label: 'Charge under Full Moonlight', icon: Moon, description: 'Amplifies intuition & feminine energy', color: '#9D4EDD' },
  { id: 'sunlight', label: 'Charge in Morning Sunlight', icon: Sun, description: 'Activates vitality & masculine energy', color: '#FFB800' },
  { id: 'both', label: 'Dual Sun & Moon Charging', icon: Sparkles, description: 'Complete energetic balance (recommended)', color: '#FF6B9D' },
];

const ExclusiveCollectionPage = () => {
  usePageTitle('The Sacred Six Collection');
  const [exclusiveStones, setExclusiveStones] = useState([]);
  const [selectedStone, setSelectedStone] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    chargingOption: 'both',
    specialInstructions: '',
    intention: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/collection/stones`)
      .then(res => res.json())
      .then(data => {
        const mapped = data.map(s => ({
          id: s.stone_id,
          name: s.name,
          subtitle: s.subtitle,
          price: s.price,
          image: s.image,
          chakra: s.chakra,
          chakraColor: s.chakra_color,
          energy: s.energy,
          description: s.description,
          benefits: s.benefits,
          available: s.available,
        }));
        setExclusiveStones(mapped);
      })
      .catch(() => {});
  }, []);

  const handleVideoToggle = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStoneSelect = (stone) => {
    setSelectedStone(stone);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('private-order-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStone) {
      toast.error('Please select a gemstone first');
      return;
    }

    setIsSubmitting(true);
    try {
      const orderData = {
        ...formData,
        stone: selectedStone.name,
        stoneId: selectedStone.id,
        price: selectedStone.price,
        chargingOption: formData.chargingOption,
      };

      const response = await fetch(`${API_URL}/api/exclusive-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });

      if (response.ok) {
        toast.success('Your exclusive order has been received! We will contact you within 24 hours.');
        setSelectedStone(null);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          chargingOption: 'both',
          specialInstructions: '',
          intention: '',
        });
      } else {
        throw new Error('Failed to submit order');
      }
    } catch (error) {
      toast.error('Order submitted! We will contact you shortly to confirm.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden bg-[#1e1919]">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          loop
          muted={isMuted}
          playsInline
          poster="https://static.prod-images.emergentagent.com/jobs/bf3299c0-3855-4a52-beac-c89141d44eb4/images/e1a4219d64e14a207f9818d692e94261abad96ce61e5ce3b2ff98f976596877e.png"
        >
          <source src="https://customer-assets.emergentagent.com/job_bf3299c0-3855-4a52-beac-c89141d44eb4/artifacts/lbt1qcg6_Welcome-GemstoneMinerals.mp4" type="video/mp4" />
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1e1919]/60 via-transparent to-[#1e1919]/90" />
        
        {/* Video Controls */}
        <div className="absolute bottom-6 left-6 flex gap-3 z-20">
          <button
            onClick={handleVideoToggle}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all"
            data-testid="video-play-btn"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={handleMuteToggle}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all"
            data-testid="video-mute-btn"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
        
        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center px-6 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Lock className="w-4 h-4 text-[#c9a96e]" />
              <span className="text-sm text-white/90 tracking-widest uppercase">Private Collection</span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight">
              The <span className="text-[#c9a96e]">Sacred Six</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
              Only 6 stones. Each one hand-selected, energetically activated, and charged specifically for you. 
              This is not a shop—it's a sacred exchange.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-white/60">
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#c9a96e]" /> Personally Activated</span>
              <span className="flex items-center gap-2"><Moon className="w-4 h-4 text-[#9D4EDD]" /> Moon Charged</span>
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#FF6B9D]" /> Energy Guaranteed</span>
            </div>
          </div>
        </div>
      </section>

      {/* Exclusivity Statement */}
      <section className="py-16 bg-gradient-to-b from-[#1e1919] to-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <Gem className="w-10 h-10 text-[#c9a96e] mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-white mb-4">Why Only Six Stones?</h2>
            <p className="text-white/70 leading-relaxed">
              Each gemstone in our collection requires <span className="text-[#c9a96e] font-semibold">72 hours of personal activation</span>. 
              We cleanse, charge, and program every stone with protective and manifestation energy before it reaches you. 
              Mass production destroys vibrational integrity. We choose quality of energy over quantity of sales.
              <span className="block mt-3 text-[#FF6B9D]">When these stones are gone, they're gone.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Exclusive Stone Collection */}
      <section className="py-20 px-6" id="collection">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.3em] text-[#9D4EDD] font-bold">Hand-Selected</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1e1919] mt-4 mb-6">
              The Sacred Collection
            </h2>
            <p className="text-[#61525a] max-w-2xl mx-auto">
              Each stone has been chosen for its exceptional energy signature. Click to select your stone and complete your private order below.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {exclusiveStones.map((stone) => (
              <div
                key={stone.id}
                className={`
                  group relative bg-white rounded-3xl overflow-hidden transition-all duration-500 cursor-pointer
                  ${selectedStone?.id === stone.id 
                    ? 'ring-4 ring-[#9D4EDD] shadow-2xl shadow-[#9D4EDD]/20 scale-[1.02]' 
                    : 'hover:shadow-2xl hover:-translate-y-2 border border-[#e8e4de]'}
                `}
                onClick={() => handleStoneSelect(stone)}
                data-testid={`stone-${stone.id}`}
              >
                {/* Selection Indicator */}
                {selectedStone?.id === stone.id && (
                  <div className="absolute top-4 right-4 z-20 bg-[#9D4EDD] text-white p-2 rounded-full">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                )}

                {/* Availability Badge */}
                <div className="absolute top-4 left-4 z-20">
                  <span className={`
                    px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
                    ${stone.available === 1 ? 'bg-[#FF4D6D] text-white' : 'bg-[#c9a96e] text-white'}
                  `}>
                    {stone.available === 1 ? 'Last One' : `Only ${stone.available} Left`}
                  </span>
                </div>

                {/* Image */}
                <div className="relative h-72 overflow-hidden bg-black">
                  <img
                    src={stone.image}
                    alt={stone.name}
                    className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Chakra Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stone.chakraColor }}
                    />
                    <span className="text-xs uppercase tracking-wider text-[#7A7A9A]">{stone.chakra} Chakra</span>
                  </div>

                  <h3 className="font-serif text-xl text-[#1e1919] mb-1">{stone.name}</h3>
                  <p className="text-sm text-[#9D4EDD] font-medium mb-3">{stone.subtitle}</p>
                  
                  <p className="text-sm text-[#61525a] line-clamp-3 mb-4">
                    {stone.description.substring(0, 150)}...
                  </p>

                  {/* Energy Type */}
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    <Sparkles className="w-4 h-4 text-[#c9a96e]" />
                    <span className="text-[#61525a]">{stone.energy}</span>
                  </div>

                  {/* Benefits */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {stone.benefits.slice(0, 2).map((benefit, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 rounded-full bg-[#f8f5ff] text-[#9D4EDD]">
                        {benefit}
                      </span>
                    ))}
                  </div>

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#e8e4de]">
                    <div>
                      <span className="text-2xl font-serif text-[#1e1919]">${stone.price}</span>
                      <span className="text-xs text-[#7A7A9A] ml-2">includes activation</span>
                    </div>
                    <button className="px-4 py-2 rounded-full bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white text-sm font-semibold hover:shadow-lg transition-all">
                      Select
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Order Form */}
      <section className="py-20 bg-gradient-to-b from-[#f8f5ff] to-[#faf8f5]" id="private-order-form">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1e1919] text-white mb-6">
              <Lock className="w-4 h-4 text-[#c9a96e]" />
              <span className="text-sm tracking-widest uppercase">Private & Secure</span>
            </div>
            <h2 className="font-serif text-4xl text-[#1e1919] mb-4">Complete Your Sacred Order</h2>
            <p className="text-[#61525a] max-w-2xl mx-auto">
              No shopping carts. No checkout lines. Just a private, personal exchange. 
              We'll contact you within 24 hours to confirm your order and arrange payment.
            </p>
          </div>

          {/* Selected Stone Display */}
          {selectedStone && (
            <div className="mb-8 p-6 bg-white rounded-2xl border-2 border-[#9D4EDD] shadow-lg" data-testid="selected-stone-display">
              <div className="flex items-center gap-4">
                <img src={selectedStone.image} alt={selectedStone.name} className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-[#1e1919]">{selectedStone.name}</h3>
                  <p className="text-sm text-[#9D4EDD]">{selectedStone.subtitle}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-serif text-[#1e1919]">${selectedStone.price}</span>
                  <p className="text-xs text-[#00C853]">Activation included</p>
                </div>
              </div>
            </div>
          )}

          {!selectedStone && (
            <div className="mb-8 p-8 bg-white/50 rounded-2xl border-2 border-dashed border-[#9D4EDD]/30 text-center">
              <Gem className="w-12 h-12 text-[#9D4EDD]/40 mx-auto mb-3" />
              <p className="text-[#7A7A9A]">Please select a stone from the collection above</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-xl">
            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="font-serif text-xl text-[#1e1919] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#9D4EDD] text-white flex items-center justify-center text-sm">1</span>
                Your Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                  data-testid="input-fullname"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                  data-testid="input-email"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                  data-testid="input-phone"
                />
              </div>
            </div>

            {/* Shipping Address */}
            <div className="mb-8">
              <h3 className="font-serif text-xl text-[#1e1919] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#9D4EDD] text-white flex items-center justify-center text-sm">2</span>
                Shipping Address
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="address"
                  placeholder="Street Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="md:col-span-2 px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                  data-testid="input-address"
                />
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                  data-testid="input-city"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="state"
                    placeholder="State"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                    data-testid="input-state"
                  />
                  <input
                    type="text"
                    name="zipCode"
                    placeholder="ZIP Code"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    className="px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all"
                    data-testid="input-zip"
                  />
                </div>
              </div>
            </div>

            {/* Charging Options */}
            <div className="mb-8">
              <h3 className="font-serif text-xl text-[#1e1919] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#9D4EDD] text-white flex items-center justify-center text-sm">3</span>
                Stone Activation Preference
              </h3>
              <p className="text-sm text-[#61525a] mb-4">
                Choose how you'd like your stone energetically charged before shipping:
              </p>
              <div className="space-y-3">
                {chargingOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`
                      flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all
                      ${formData.chargingOption === option.id 
                        ? 'bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 border-2 border-[#9D4EDD]' 
                        : 'bg-[#faf8f5] border-2 border-transparent hover:border-[#e8e4de]'}
                    `}
                    data-testid={`charging-${option.id}`}
                  >
                    <input
                      type="radio"
                      name="chargingOption"
                      value={option.id}
                      checked={formData.chargingOption === option.id}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${option.color}20` }}
                    >
                      <option.icon className="w-6 h-6" style={{ color: option.color }} />
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold text-[#1e1919] block">{option.label}</span>
                      <span className="text-sm text-[#7A7A9A]">{option.description}</span>
                    </div>
                    {formData.chargingOption === option.id && (
                      <CheckCircle className="w-6 h-6 text-[#9D4EDD]" />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Intention Setting */}
            <div className="mb-8">
              <h3 className="font-serif text-xl text-[#1e1919] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#9D4EDD] text-white flex items-center justify-center text-sm">4</span>
                Your Intention (Optional)
              </h3>
              <p className="text-sm text-[#61525a] mb-4">
                Share your intention for this stone. We'll program it with your specific energy signature.
              </p>
              <textarea
                name="intention"
                placeholder="Example: I intend to use this stone for attracting abundance and opening new opportunities in my business..."
                value={formData.intention}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all resize-none"
                data-testid="input-intention"
              />
            </div>

            {/* Special Instructions */}
            <div className="mb-8">
              <h3 className="font-serif text-xl text-[#1e1919] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#9D4EDD] text-white flex items-center justify-center text-sm">5</span>
                Special Instructions (Optional)
              </h3>
              <textarea
                name="specialInstructions"
                placeholder="Any special requests or notes for your order..."
                value={formData.specialInstructions}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all resize-none"
                data-testid="input-instructions"
              />
            </div>

            {/* Submit */}
            <div className="text-center">
              <button
                type="submit"
                disabled={!selectedStone || isSubmitting}
                className={`
                  inline-flex items-center gap-3 px-12 py-4 rounded-xl font-semibold text-white text-lg
                  transition-all duration-300
                  ${selectedStone && !isSubmitting
                    ? 'bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] hover:shadow-2xl hover:shadow-[#9D4EDD]/30 hover:scale-105'
                    : 'bg-[#ccc] cursor-not-allowed'}
                `}
                data-testid="submit-order-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Submit Private Order
                  </>
                )}
              </button>
              <p className="mt-4 text-sm text-[#7A7A9A]">
                We'll contact you within 24 hours to confirm your order and arrange secure payment.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-[#1e1919]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <Shield className="w-10 h-10 text-[#c9a96e] mx-auto mb-4" />
              <h3 className="font-serif text-lg text-white mb-2">Energy Guaranteed</h3>
              <p className="text-white/60 text-sm">Every stone tested & activated</p>
            </div>
            <div>
              <Heart className="w-10 h-10 text-[#FF6B9D] mx-auto mb-4" />
              <h3 className="font-serif text-lg text-white mb-2">Personal Touch</h3>
              <p className="text-white/60 text-sm">Charged with your intentions</p>
            </div>
            <div>
              <Star className="w-10 h-10 text-[#FFB800] mx-auto mb-4" />
              <h3 className="font-serif text-lg text-white mb-2">Sacred Exchange</h3>
              <p className="text-white/60 text-sm">Private, respectful service</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExclusiveCollectionPage;
